import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import type { EChartsOption } from 'echarts';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';

import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { TimeReclaimedData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

const BREAKDOWN_COLORS = ['#FB7185', '#FBBF24', '#34D399', '#60A5FA', '#A78BFA', '#F97316'];

@Component({
    selector: 'app-time-reclaimed-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule, IconComponent, NgxEchartsModule, SkeletonModule],
    providers: [
        { provide: NGX_ECHARTS_CONFIG, useValue: { echarts: () => import('echarts') } },
    ],
    templateUrl: './time-reclaimed-widget.component.html',
    styleUrl: './time-reclaimed-widget.component.scss',
})
export class TimeReclaimedWidgetComponent implements OnInit {
    readonly data = signal<TimeReclaimedData | null>(null);
    readonly loading = signal(false);

    readonly breakdownWithColors = computed(() =>
        (this.data()?.breakdown ?? []).map((item, i) => ({
            ...item,
            color: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
        }))
    );

    readonly pctChange = computed(() => this.data()?.percentageChangeFromLastMonth ?? null);

    readonly hoursDiff = computed(() => {
        const pct = this.pctChange();
        const current = this.data()?.reclaimedHours ?? 0;
        if (pct === null || pct === 0) return null;
        // diff = current × pct / (100 + pct)
        return current * pct / (100 + pct);
    });

    readonly chartOption = computed<EChartsOption>(() => {
        const items = this.breakdownWithColors();
        const totalHours = Math.max(this.data()?.reclaimedHours ?? 1, 1);

        // One ring per breakdown item, stacked concentrically
        const ringWidth = 8;   // % of chart width per ring
        const ringGap = 2;    // % gap between rings
        const outermost = 96;  // outer edge of the first (outermost) ring

        const series = items.map((item, i) => {
            const outerR = outermost - i * (ringWidth + ringGap);
            const innerR = outerR - ringWidth;
            const progress = Math.min(item.hoursReclaimed / totalHours, 1);

            return {
                name: item.label,
                type: 'pie' as const,
                startAngle: 180,
                endAngle: 360,
                radius: [`${innerR}%`, `${outerR}%`],
                center: ['50%', '100%'],
                label: { show: false },
                labelLine: { show: false },
                data: [
                    {
                        value: progress,
                        name: item.label,
                        itemStyle: { color: item.color, borderRadius: 8 },
                    },
                    {
                        value: 1 - progress,
                        name: '',
                        itemStyle: { color: item.color + '22', borderRadius: 8 },
                        emphasis: { disabled: true },
                    },
                ],
            };
        });

        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => params.name
                    ? `${params.name}: ${(params.value * totalHours)} hrs`
                    : '',
            },
            legend: { show: false },
            series,
        };
    });

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            const mode = this.state.viewMode();
            const period = this.state.period();
            const range = this.state.effectiveRange();
            const workspaceId = this.state.workspaceId();
            this.fetch(mode, period, range, workspaceId);
        });
    }

    ngOnInit(): void { }

    formatHours(value: number): string {
        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
        if (value % 1 !== 0) return value.toFixed(1);
        return String(value);
    }

    formatCost(value: number): string {
        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
        return String(Math.round(value));
    }

    private fetch(
        mode: ReturnType<WorkspaceOverviewStateService['viewMode']>,
        period: ReturnType<WorkspaceOverviewStateService['period']>,
        range: ReturnType<WorkspaceOverviewStateService['effectiveRange']>,
        workspaceId: string,
    ): void {
        this.loading.set(true);
        this.service.getTimeReclaimed({ mode, period, range, workspaceId }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: () => this.loading.set(false),
        });
    }
}
